import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';

const Login = () => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotMessage, setShowForgotMessage] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(identifier, password);
      navigate(from, { replace: true });
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <link href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css" rel="stylesheet" />
      
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.logo}>
            <i className="fas fa-graduation-cap" style={styles.logoIcon}></i>
            <span style={styles.logoText}>Smart Learning Portal</span>
          </div>
        </div>
      </header>

      <div style={styles.loginSection}>
        <div style={styles.loginContainer}>
          <div style={styles.loginCard}>
            <div style={styles.loginHeader}>
              <h1 style={styles.loginTitle}>Welcome Back</h1>
              <p style={styles.loginSubtitle}>Sign in to your account to continue</p>
            </div>

            {error && (
              <div style={styles.errorAlert}>
                <i className="fas fa-exclamation-circle" style={styles.errorIcon}></i>
                <span style={styles.errorText}>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} style={styles.loginForm}>
              <div style={styles.inputGroup}>
                <label htmlFor="identifier" style={styles.inputLabel}>
                  <i className="fas fa-user" style={styles.inputIcon}></i>
                  Email or Username
                </label>
                <input
                  type="text"
                  style={styles.inputField}
                  id="identifier"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="Enter your email or username"
                  required
                />
              </div>

              <div style={styles.inputGroup}>
                <label htmlFor="password" style={styles.inputLabel}>
                  <i className="fas fa-lock" style={styles.inputIcon}></i>
                  Password
                </label>
                <div style={styles.passwordWrapper}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    style={{...styles.inputField, ...styles.inputWithToggle}}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPassword(s => !s)}
                    style={styles.eyeButton}
                  >
                    <i className={showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
                  </button>
                </div>
              </div>

              <div style={styles.formOptions}>
                <label style={styles.rememberMe}>
                  <input type="checkbox" style={styles.checkbox} />
                  <span style={styles.checkboxLabel}>Remember me</span>
                </label>
                <a
                  href="#"
                  style={styles.forgotPassword}
                  onClick={(e) => {
                    e.preventDefault();
                    setShowForgotMessage(true);
                  }}
                >
                  Forgot password?
                </a>
              </div>

              <button
                type="submit"
                style={{
                  ...styles.loginButton,
                  ...(loading ? styles.loginButtonLoading : {})
                }}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div style={styles.spinner}></div>
                    Signing in...
                  </>
                ) : (
                  <>
                    <i className="fas fa-sign-in-alt" style={styles.buttonIcon}></i>
                    Sign In
                  </>
                )}
              </button>
            </form>

            {showForgotMessage && (
              <div style={styles.infoAlert} role="status">
                <i className="fas fa-info-circle" style={styles.infoIcon}></i>
                <div style={styles.infoText}>
                  If you've forgotten your password, please contact your support
                  team to request a password reset. They will verify your identity and assist with account
                  recovery.
                </div>
              </div>
            )}

            <div style={styles.signupSection}>
              <p style={styles.signupText}>
                Don't have an account?{' '}
                <Link to="/register" style={styles.signupLink}>Create account</Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* <footer style={styles.footer}>
        <div style={styles.footerContent}>
          <p style={styles.footerText}>
            © 2025 Cognify Analytics. All rights reserved.
          </p>
          <div style={styles.footerLinks}>
            <a href="#" style={styles.footerLink}>Privacy Policy</a>
            <a href="#" style={styles.footerLink}>Terms of Service</a>
            <a href="#" style={styles.footerLink}>Contact Support</a>
          </div>
        </div>
      </footer> */}
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    fontFamily: '"Inter", sans-serif',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e5e7eb',
    padding: '16px 0',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  headerContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  logoIcon: {
    fontSize: '32px',
    color: '#4f46e5',
  },
  logoText: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#111827',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  loginSection: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 24px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  loginContainer: {
    width: '100%',
    maxWidth: '440px',
  },
  loginCard: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    padding: '40px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    border: '1px solid #e5e7eb',
  },
  loginHeader: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  loginTitle: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#111827',
    margin: '0 0 8px 0',
  },
  loginSubtitle: {
    fontSize: '16px',
    color: '#6b7280',
    margin: 0,
  },
  errorAlert: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  errorIcon: {
    color: '#dc2626',
    fontSize: '16px',
  },
  errorText: {
    color: '#dc2626',
    fontSize: '14px',
    fontWeight: '500',
  },
  infoAlert: {
    backgroundColor: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: '8px',
    padding: '12px 16px',
    marginTop: '20px',
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
    color: '#1e40af',
    fontSize: '14px',
  },
  infoIcon: {
    color: '#1e40af',
    fontSize: '18px',
    marginTop: '2px',
  },
  infoText: {
    lineHeight: '1.4',
  },
  loginForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  inputLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  inputIcon: {
    color: '#6b7280',
    fontSize: '14px',
    width: '16px',
  },
  inputField: {
    padding: '12px 16px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '16px',
    color: '#111827',
    backgroundColor: '#ffffff',
    transition: 'all 0.2s ease',
    fontFamily: '"Inter", sans-serif',
  },
  formOptions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: '14px',
  },
  rememberMe: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
  },
  checkbox: {
    width: '16px',
    height: '16px',
    borderRadius: '4px',
    border: '1px solid #d1d5db',
    cursor: 'pointer',
  },
  checkboxLabel: {
    color: '#374151',
  },
  forgotPassword: {
    color: '#4f46e5',
    textDecoration: 'none',
    fontWeight: '500',
    transition: 'color 0.2s ease',
  },
  loginButton: {
    backgroundColor: '#4f46e5',
    border: 'none',
    borderRadius: '8px',
    padding: '14px 20px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#ffffff',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontFamily: '"Inter", sans-serif',
  },
  loginButtonLoading: {
    opacity: 0.7,
    cursor: 'not-allowed',
  },
  spinner: {
    width: '16px',
    height: '16px',
    border: '2px solid transparent',
    borderTop: '2px solid #ffffff',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  passwordWrapper: {
    position: 'relative',
    width: '100%',
  },
  inputWithToggle: {
    width: '100%',
    paddingRight: '40px',
    boxSizing: 'border-box',
  },
  eyeButton: {
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#6b7280',
    fontSize: '16px',
    padding: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonIcon: {
    fontSize: '16px',
  },
  signupSection: {
    marginTop: '24px',
    textAlign: 'center',
    paddingTop: '24px',
    borderTop: '1px solid #e5e7eb',
  },
  signupText: {
    fontSize: '14px',
    color: '#6b7280',
    margin: 0,
  },
  signupLink: {
    color: '#4f46e5',
    textDecoration: 'none',
    fontWeight: '600',
    transition: 'color 0.2s ease',
  },
  footer: {
    backgroundColor: '#111827',
    color: '#ffffff',
    padding: '24px 0',
  },
  footerContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '16px',
  },
  footerText: {
    fontSize: '14px',
    color: '#9ca3af',
    margin: 0,
  },
  footerLinks: {
    display: 'flex',
    gap: '24px',
  },
  footerLink: {
    fontSize: '14px',
    color: '#9ca3af',
    textDecoration: 'none',
    transition: 'color 0.2s ease',
  },
};

const additionalStyles = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  input:focus {
    outline: none;
    border-color: #4f46e5;
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
  }

  button:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .forgot-password:hover {
    color: #3730a3;
  }

  .signup-link:hover {
    color: #3730a3;
  }

  .footer-link:hover {
    color: #ffffff;
  }

  @media (max-width: 640px) {
    .login-card {
      padding: 24px;
      margin: 16px;
    }
    
    .form-options {
      flex-direction: 'column';
      align-items: 'flex-start';
      gap: '12px';
    }
    
    .footer-content {
      flex-direction: 'column';
      text-align: 'center';
    }
    
    .footer-links {
      justify-content: 'center';
    }
  }
`;

const styleSheet = document.createElement('style');
styleSheet.innerText = additionalStyles;
document.head.appendChild(styleSheet);

export default Login;